import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import {
  AlertTriangle,
  Eye,
  Plus,
  MoreHorizontal,
  Receipt,
  XCircle,
  Trash2,
} from "lucide-react";
import { useState, type ComponentProps } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";

import { InvoiceCancelDialog } from "~/components/engagement/admin/invoice-cancel-dialog";
import { InvoiceDeleteDialog } from "~/components/engagement/admin/invoice-delete-dialog";
import { PaymentBreakdownPopup } from "~/components/payment-breakdown-popup";
import { StripePayoutsCard } from "~/components/stripe-payouts-card";
import { Badge } from "~/components/ui/badge";
import { Button, ButtonRouter, buttonVariants } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { Modal } from "~/components/ui/modal/modal";
import { PageHeader } from "~/components/ui/page-title";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { cn } from "~/lib/cn";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import {
  voidInvoiceForCoachFn,
  deleteInvoiceForCoachFn,
} from "~/lib/functions/invoice";
import { helpVideosQueryOptions } from "~/lib/queries/learningQuery";
import { CreateInvoiceSchema } from "~/lib/schemas/invoice";
import type { InvoiceStatus } from "~/lib/server/invoice";
import {
  COACH_CURRENCIES,
  type CoachCurrency,
} from "~/lib/shared/constants/currencies";
import {
  amountInputStep,
  toMajorUnits,
  toMinorUnits,
} from "~/lib/shared/currency";

const currencyOptions = COACH_CURRENCIES;

type CurrencyCode = CoachCurrency;

const isCurrencyCode = (value: string): value is CurrencyCode =>
  currencyOptions.some(
    (currency) => currency.toLowerCase() === value.toLowerCase(),
  );

const INVOICE_STATUSES = [
  "draft",
  "open",
  "paid",
  "uncollectible",
  "void",
] as const satisfies readonly InvoiceStatus[];

const isInvoiceStatus = (value: string): value is InvoiceStatus =>
  (INVOICE_STATUSES as readonly string[]).includes(value);

const getInvoicesForCoachData = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { getInvoicesForCoach } = await import("~/lib/server/invoice");
    const invoices = await getInvoicesForCoach(context.coach.id);

    return invoices.map((invoice) => {
      if (!isCurrencyCode(invoice.currency)) {
        throw new Error(
          `Invalid currency "${invoice.currency}" on invoice ${invoice.id} for coach ${context.coach.id}`,
        );
      }
      if (!isInvoiceStatus(invoice.status)) {
        throw new Error(
          `Invalid status "${invoice.status}" on invoice ${invoice.id} for coach ${context.coach.id}`,
        );
      }

      return {
        id: invoice.id,
        customerName: invoice.customerName,
        customerEmail: invoice.customerEmail,
        status: invoice.status,
        amount: invoice.amount,
        createdOn: invoice.createdAt ? new Date(invoice.createdAt) : null,
        paymentDate: invoice.paidOn ? new Date(invoice.paidOn) : null,
        currency: invoice.currency,
        stripeChargeId: invoice.stripeChargeId,
        stripeInvoiceId: invoice.stripeInvoiceId,
        url: invoice.url,
        stripeFee: invoice.stripeFee,
        platformFee: invoice.platformFee,
        transferAmount: invoice.transferAmount,
        platformFeeBps: invoice.platformFeeBps,
        settlementCurrency: invoice.settlementCurrency,
      };
    });
  });

type InvoiceFormData = z.infer<typeof CreateInvoiceSchema>;

const createInvoiceAction = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(CreateInvoiceSchema)
  .handler(async ({ data, context }) => {
    const { createInvoiceForCoach } = await import("~/lib/server/invoice");
    await createInvoiceForCoach(
      data.customerName,
      data.customerEmail,
      data.description,
      toMinorUnits(data.amount, data.currency),
      data.currency,
      new Date(data.dueDate),
      context.coach,
    );

    return { success: true };
  });

type BadgeVariant = NonNullable<ComponentProps<typeof Badge>["variant"]>;

const STATUS_BADGES: Record<
  InvoiceStatus,
  { variant: BadgeVariant; className?: string }
> = {
  paid: { variant: "success" },
  uncollectible: { variant: "danger" },
  void: { variant: "cancelled" },
  draft: {
    variant: "outline",
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  open: {
    variant: "outline",
    className: "border-purple-200 bg-purple-50 text-purple-700",
  },
};

const StatusBadge = ({ status }: { status: InvoiceStatus }) => {
  const { variant, className } = STATUS_BADGES[status];
  return (
    <Badge variant={variant} size="sm" className={cn("capitalize", className)}>
      {status}
    </Badge>
  );
};

const formatCurrency = (amount: number, currency: CurrencyCode) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(toMajorUnits(amount, currency));
};

export const Route = createFileRoute("/_user/my-coaching/clients/invoices")({
  staticData: { title: "Invoices" },
  // Every coach works here from the day she registers, including one under
  // review. A revoked coach goes to the dashboard, which shows her revoked
  // access.
  beforeLoad: ({ context }) => {
    if (context.isRevoked) {
      throw redirect({ to: "/my-coaching/clients/dashboard" });
    }
  },
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["coach-invoices"],
        queryFn: () => getInvoicesForCoachData(),
      }),
      context.queryClient
        .ensureQueryData(helpVideosQueryOptions())
        .catch(() => undefined),
    ]);
    return {};
  },
  component: InvoicesPage,
});

function InvoicesPage() {
  const coach = useCoachPrivateProfileRequired();

  const { register, handleSubmit, formState, reset, control, watch } =
    useForm<InvoiceFormData>({
      resolver: zodResolver(CreateInvoiceSchema),
      defaultValues: {
        customerName: "",
        customerEmail: "",
        description: "",
        currency: currencyOptions[0],
        amount: 0,
        dueDate: "",
      },
    });

  const { errors } = formState;
  const watchedCurrency = watch("currency");
  const [createInvoiceVisible, setCreateInvoiceVisible] = useState(false);
  const [cancelDialogInvoice, setCancelDialogInvoice] = useState<{
    id: string;
    stripeInvoiceId: string;
    customerName: string;
  } | null>(null);
  const [deleteDialogInvoice, setDeleteDialogInvoice] = useState<{
    id: string;
    stripeInvoiceId: string;
    customerName: string;
  } | null>(null);

  const queryClient = useQueryClient();

  const { data: invoices = [] } = useQuery({
    queryKey: ["coach-invoices"],
    queryFn: () => getInvoicesForCoachData(),
  });

  const createInvoiceMutation = useMutation({
    mutationFn: (data: InvoiceFormData) => createInvoiceAction({ data }),
    onSuccess: () => {
      toast.success("Invoice created");
      queryClient.invalidateQueries({ queryKey: ["coach-invoices"] });
      setCreateInvoiceVisible(false);
      reset();
    },
    onError: () => {
      toast.error("Failed to create invoice");
    },
  });

  const voidInvoiceMutation = useMutation({
    mutationFn: voidInvoiceForCoachFn,
    onSuccess: () => {
      toast.success("Invoice cancelled");
      queryClient.invalidateQueries({ queryKey: ["coach-invoices"] });
      setCancelDialogInvoice(null);
    },
    onError: () => {
      toast.error("Failed to cancel invoice");
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: deleteInvoiceForCoachFn,
    onSuccess: () => {
      toast.success("Invoice deleted");
      queryClient.invalidateQueries({ queryKey: ["coach-invoices"] });
      setDeleteDialogInvoice(null);
    },
    onError: () => {
      toast.error("Failed to delete invoice");
    },
  });

  const handleCancelInvoice = (invoice: {
    id: string;
    stripeInvoiceId: string;
    customerName: string;
  }) => {
    setCancelDialogInvoice(invoice);
  };

  const handleConfirmCancel = () => {
    if (!cancelDialogInvoice) return;
    voidInvoiceMutation.mutate({
      data: {
        stripeInvoiceId: cancelDialogInvoice.stripeInvoiceId,
        invoiceId: cancelDialogInvoice.id,
      },
    });
  };

  const handleDeleteInvoice = (invoice: {
    id: string;
    stripeInvoiceId: string;
    customerName: string;
  }) => {
    setDeleteDialogInvoice(invoice);
  };

  const handleConfirmDelete = () => {
    if (!deleteDialogInvoice) return;
    deleteInvoiceMutation.mutate({
      data: {
        stripeInvoiceId: deleteDialogInvoice.stripeInvoiceId,
        invoiceId: deleteDialogInvoice.id,
      },
    });
  };

  const canSendInvoices = coach.stripeChargesEnabled && coach.contractSigned;

  const onSubmit = (data: InvoiceFormData) => {
    createInvoiceMutation.mutate(data);
  };

  return (
    <div className="space-y-8">
      <PageHeader
        help="help_invoices"
        title="Invoices"
        description="Create and send invoices to your customers."
        actions={
          canSendInvoices && (
            <Modal.Root
              open={createInvoiceVisible}
              onOpenChange={setCreateInvoiceVisible}
            >
              <Modal.Trigger
                className={cn(
                  buttonVariants({
                    variant: "default",
                  }),
                )}
                onClick={() => setCreateInvoiceVisible(true)}
              >
                <Plus className="mr-2" />
                Create invoice
              </Modal.Trigger>
              <Modal.Panel>
                <Modal.Title>Create new invoice</Modal.Title>
                <form
                  className="mt-4 flex flex-col gap-4"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <FormField
                    label="Customer name"
                    required
                    helpText="Customer's full name"
                    error={errors.customerName?.message}
                  >
                    <Input
                      type="text"
                      {...register("customerName")}
                      error={!!errors.customerName}
                    />
                  </FormField>

                  <FormField
                    label="Email"
                    required
                    helpText="Customer's email address"
                    error={errors.customerEmail?.message}
                  >
                    <Input
                      type="email"
                      {...register("customerEmail")}
                      error={!!errors.customerEmail}
                    />
                  </FormField>

                  <FormField
                    label="Description"
                    required
                    helpText="Description of the invoice"
                    error={errors.description?.message}
                  >
                    <Input
                      type="text"
                      {...register("description")}
                      error={!!errors.description}
                    />
                  </FormField>

                  <FormField
                    label="Amount"
                    required
                    helpText="Amount in selected currency"
                    error={errors.amount?.message}
                  >
                    <Input
                      type="number"
                      step={amountInputStep(watchedCurrency)}
                      {...register("amount", { valueAsNumber: true })}
                      error={!!errors.amount}
                    />
                  </FormField>

                  <FormField
                    label="Currency"
                    required
                    error={errors.currency?.message}
                  >
                    <Controller
                      control={control}
                      name="currency"
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            className="w-full justify-between"
                            aria-invalid={!!errors.currency}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {currencyOptions.map((currency) => (
                              <SelectItem key={currency} value={currency}>
                                {currency}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </FormField>

                  <FormField
                    label="Due date"
                    required
                    helpText="Date when payment is due"
                    error={errors.dueDate?.message}
                  >
                    <Input
                      type="date"
                      {...register("dueDate")}
                      error={!!errors.dueDate}
                    />
                  </FormField>

                  <Button
                    type="submit"
                    disabled={createInvoiceMutation.isPending}
                  >
                    {createInvoiceMutation.isPending
                      ? "Creating..."
                      : "Create invoice"}
                  </Button>
                </form>
              </Modal.Panel>
            </Modal.Root>
          )
        }
      />

      {!canSendInvoices && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="size-4 shrink-0 text-amber-800" />
          <div className="flex-1 space-y-1">
            <p className="text-base font-medium text-gray-900">
              Payment setup required
            </p>
            <p className="text-text-secondary text-sm">
              {!coach.stripeChargesEnabled &&
                "Connect your Stripe account to start sending invoices. "}
              {!coach.contractSigned &&
                "Sign your coach agreement to start sending invoices."}
            </p>
          </div>
          <ButtonRouter to="/my-coaching/stripe-connect" size="sm">
            Set up
          </ButtonRouter>
        </div>
      )}

      {canSendInvoices && <StripePayoutsCard />}

      <InvoiceCancelDialog
        isOpen={!!cancelDialogInvoice}
        onClose={() => setCancelDialogInvoice(null)}
        onConfirm={handleConfirmCancel}
        customerName={cancelDialogInvoice?.customerName ?? ""}
        isLoading={voidInvoiceMutation.isPending}
      />
      <InvoiceDeleteDialog
        isOpen={!!deleteDialogInvoice}
        onClose={() => setDeleteDialogInvoice(null)}
        onConfirm={handleConfirmDelete}
        customerName={deleteDialogInvoice?.customerName ?? ""}
        isLoading={deleteInvoiceMutation.isPending}
      />

      {invoices.length === 0 ? (
        <EmptyState
          variant="hero"
          size="lg"
          icon={Receipt}
          title="No invoices yet"
          description="When you bill a client, their invoices show up here so you can track every transaction in one place."
          className="min-h-[50vh]"
        >
          {canSendInvoices && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCreateInvoiceVisible(true)}
            >
              Create invoice
            </Button>
          )}
        </EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Created on</TableHead>
              <TableHead>Paid on</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => {
              const canCancel = invoice.status === "open";
              const canDelete = invoice.status === "draft";
              const hasActions = canCancel || canDelete || !!invoice.url;

              return (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <p>{invoice.customerName}</p>
                    <p className="text-text-tertiary text-xs">
                      {invoice.customerEmail}
                    </p>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={invoice.status} />
                  </TableCell>
                  <TableCell className="flex items-center gap-2">
                    {formatCurrency(invoice.amount, invoice.currency)}
                    {invoice.status === "paid" && (
                      <PaymentBreakdownPopup
                        data={{
                          amount: invoice.amount,
                          currency: invoice.currency,
                          stripeFee: invoice.stripeFee,
                          platformFee: invoice.platformFee,
                          transferAmount: invoice.transferAmount,
                          platformFeeBps: invoice.platformFeeBps,
                          settlementCurrency: invoice.settlementCurrency,
                          paidOn: invoice.paymentDate,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {invoice.createdOn &&
                      Intl.DateTimeFormat(undefined).format(invoice.createdOn)}
                  </TableCell>
                  <TableCell>
                    {invoice.paymentDate &&
                      Intl.DateTimeFormat(undefined).format(
                        invoice.paymentDate,
                      )}
                  </TableCell>
                  <TableCell>
                    {hasActions && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {invoice.url && (
                            <DropdownMenuItem
                              onClick={() =>
                                window.open(
                                  invoice.url ?? "",
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() =>
                              canCancel
                                ? handleCancelInvoice(invoice)
                                : undefined
                            }
                            disabled={!canCancel}
                            className={
                              !canCancel
                                ? "cursor-not-allowed opacity-50"
                                : "text-red-700"
                            }
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            Cancel
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              canDelete
                                ? handleDeleteInvoice(invoice)
                                : undefined
                            }
                            disabled={!canDelete}
                            className={
                              !canDelete
                                ? "cursor-not-allowed opacity-50"
                                : "text-red-700"
                            }
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
